import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ROUTES } from "./constants";
import { ROLES } from "../constants/roles";
import ProtectedRoute from "./ProtectedRoute";
import DashboardLayout from "../layouts/DashboardLayout/DashboardLayout";
import ErrorBoundary from "../components/ErrorBoundary";
import RoleRoute from "./RoleRoute";
import AllFiles from "files/AllFiles";
import { R } from "@tanstack/react-query-devtools/build/legacy/ReactQueryDevtools-Cn7cKi7o";

// Auth pages
const Login = React.lazy(() => import("../pages/auth/login/Login"));
const SignUp = React.lazy(() => import("../pages/auth/signup/SignUp"));
const ResetPassword = React.lazy(
  () => import("../pages/auth/reset-password/ResetPassword"),
);
const VerifyEmail = React.lazy(
  () => import("../pages/auth/verify/VerifyEmail"),
);
const SharePointSetup = React.lazy(
  () => import("../pages/auth/sharepoint/SharePointSetup"),
);

// Dashboard pages
const Dashboard = React.lazy(() => import("../pages/dashboard/home/Dashboard"));
const QmsStructure = React.lazy(
  () => import("../pages/dashboard/qms-structure/components/list/QmsStructure"),
);
const UserManagement = React.lazy(
  () => import("../pages/dashboard/user-management/users/list/UserManagement"),
);
const UsersList = React.lazy(
  () => import("../pages/dashboard/user-management/users/list/UserManagement"),
);
const QmsCreate = React.lazy(
  () =>
    import(
      "../pages/dashboard/qms-elements//other-qms-elements/create-qms/QmsCreate"
    ),
);
const QmsDetails = React.lazy(
  () =>
    import("../pages/dashboard/qms-structure/components/details/QmsDetails"),
);
const QmsEdit = React.lazy(
  () =>
    import(
      "../pages/dashboard/qms-elements/other-qms-elements/edit-qms/QmsEdit"
    ),
);
const QmsElements = React.lazy(
  () =>
    import("../pages/dashboard/qms-elements/other-qms-elements/QmsElements"),
);
const InternalOrganization = React.lazy(
  () =>
    import(
      "../pages/dashboard/qms-elements/internal-organization/InternalOrganization"
    ),
);
const ThirdPartyUsers = React.lazy(
  () =>
    import("../pages/dashboard/qms-elements/third-party-users/ThirdPartyUsers"),
);
const AddUser = React.lazy(
  () => import("../pages/dashboard/user-management/users/add/AddUser"),
);
const EditUser = React.lazy(
  () => import("../pages/dashboard/user-management/users/edit/EditUser"),
);
const EditHierarchy = React.lazy(
  () =>
    import(
      "../pages/dashboard/qms-elements//other-qms-elements/edit-hierarchy/EditHierarchy"
    ),
);
const MyDocuments = React.lazy(
  () => import("../pages/dashboard/documents/my-documents/MyDocuments"),
);
const DocumentCreate = React.lazy(
  () => import("../pages/dashboard/documents/document-create/DocumentCreate"),
);
const MyTasks = React.lazy(() => import("../pages/dashboard/my-tasks/layout"));
const My_Tasks_documents = React.lazy(
  () => import("../pages/dashboard/my-tasks/my-documents/MyDocuments"),
);
const My_tasks_MasterFiles = React.lazy(
  () => import("../pages/dashboard/my-tasks/my-master-files/MyMasterFiles"),
);
const OwnerUserManagement = React.lazy(
  () => import("../pages/dashboard/owner-user-management/OwnerUserManagement"),
);
const CreateUpdateUser = React.lazy(
  () =>
    import(
      "../pages/dashboard/owner-user-management/create-update-user/CreateUpdateUser"
    ),
);
const ViewUser = React.lazy(
  () => import("../pages/dashboard/owner-user-management/view/ViewUser"),
);
const Helpdesk = React.lazy(
  () => import("../pages/dashboard/helpdesk/Helpdesk"),
);
const MasterFiles = React.lazy(
  () => import("../pages/dashboard/master-files/MasterFiles"),
);
const Registers = React.lazy(() => import("../pages/dashboard/registers"));

const Records = React.lazy(() => import("../pages/dashboard/records/Records"));
const TenantManagement = React.lazy(
  () => import("../pages/dashboard/tenant-management/TenantManagement"),
);
const TenantManagementCreate = React.lazy(
  () =>
    import("../pages/dashboard/tenant-management/create-tenant/createTenant"),
);
const TenantManagementView = React.lazy(
  () => import("../pages/dashboard/tenant-management/view-details/viewDetails"),
);
const SubscriptionBilling = React.lazy(
  () =>
    import(
      "../pages/dashboard/subscription-billing-management/SubscriptionBillingManagement"
    ),
);
const ViewInvoices = React.lazy(
  () =>
    import(
      "../pages/dashboard/subscription-billing-management/view-invoices/ViewInvoices"
    ),
);
const InvoiceDetails = React.lazy(
  () =>
    import(
      "../pages/dashboard/subscription-billing-management/invoice-details/InvoiceDetails"
    ),
);
const QualityIssues = React.lazy(
  () => import("../pages/dashboard/quality-issues/QualityIssues"),
);

// Add import for User Details
const UserDetails = React.lazy(
  () => import("../pages/dashboard/user-management/users/details/UserDetails"),
);
const UserGroupDetails = React.lazy(
  () =>
    import(
      "../pages/dashboard/user-management/users-groups/list/UserGroupDetails"
    ),
);
const UserGroupUserDetails = React.lazy(
  () =>
    import(
      "../pages/dashboard/user-management/users-groups/user-details/UserDetails"
    ),
);

const UserGroups = React.lazy(
  () => import("../pages/dashboard/user-management/users-groups/UserGroups"),
);

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Auth Routes */}
      <Route
        path={ROUTES.AUTH.LOGIN}
        element={
          <ErrorBoundary>
            <Login />
          </ErrorBoundary>
        }
      />
      <Route
        path={ROUTES.AUTH.SIGNUP}
        element={
          <ErrorBoundary>
            <SignUp />
          </ErrorBoundary>
        }
      />
      <Route
        path={ROUTES.AUTH.RESET_PASSWORD}
        element={
          <ErrorBoundary>
            <ResetPassword />
          </ErrorBoundary>
        }
      />
      <Route
        path={ROUTES.AUTH.VERIFY_EMAIL}
        element={
          <ErrorBoundary>
            <VerifyEmail />
          </ErrorBoundary>
        }
      />
      <Route
        path={ROUTES.AUTH.SHAREPOINT_SETUP}
        element={
          <ErrorBoundary>
            <SharePointSetup />
          </ErrorBoundary>
        }
      />
      {/* Protected Dashboard Routes */}
      <Route
        path={ROUTES.DASHBOARD.HOME}
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        {/* Archivist Routes */}
        <Route
          path={ROUTES.DASHBOARD.QMS.LIST}
          element={
            <RoleRoute roles={[ROLES.ARCHIVIST]} element={<QmsStructure />} />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.CREATE}
          element={
            <RoleRoute roles={[ROLES.ARCHIVIST]} element={<QmsCreate />} />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.QMS.DETAILS}
          element={
            <RoleRoute roles={[ROLES.ARCHIVIST]} element={<QmsDetails />} />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.EDIT}
          element={
            <RoleRoute roles={[ROLES.ARCHIVIST]} element={<QmsEdit />} />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.QMS_ELEMENTS.ELEMENTS}
          element={
            <RoleRoute
              roles={[ROLES.ARCHIVIST, ROLES.OWNER, ROLES.MANAGER]}
              element={<QmsElements />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.QMS_ELEMENTS.INTERNAL_ORGANIZATION}
          element={
            <RoleRoute
              roles={[ROLES.ARCHIVIST, ROLES.OWNER, ROLES.MANAGER]}
              element={<InternalOrganization />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.QMS_ELEMENTS.THIRD_PARTY_USERS}
          element={
            <RoleRoute
              roles={[ROLES.ARCHIVIST, ROLES.OWNER, ROLES.MANAGER]}
              element={<ThirdPartyUsers />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.QMS_ELEMENTS_EDIT}
          element={
            <RoleRoute
              roles={[ROLES.ARCHIVIST, ROLES.OWNER, ROLES.MANAGER]}
              element={<EditHierarchy />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.USER_MANAGEMENT.USERS}
          element={
            <RoleRoute roles={[ROLES.ARCHIVIST]} element={<UserManagement />} />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.USER_MANAGEMENT.BASE}
          element={
            <RoleRoute roles={[ROLES.ARCHIVIST]} element={<UserManagement />} />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.USER_MANAGEMENT.USERS}
          element={
            <RoleRoute roles={[ROLES.ARCHIVIST]} element={<UsersList />} />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.USER_MANAGEMENT.USERS_ADD}
          element={
            <RoleRoute roles={[ROLES.ARCHIVIST]} element={<AddUser />} />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.USER_MANAGEMENT.USERS_EDIT}
          element={
            <RoleRoute roles={[ROLES.ARCHIVIST]} element={<EditUser />} />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.USER_MANAGEMENT.USERS_DETAILS}
          element={
            <RoleRoute roles={[ROLES.ARCHIVIST]} element={<UserDetails />} />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.USER_MANAGEMENT.USER_GROUPS}
          element={
            <RoleRoute roles={[ROLES.ARCHIVIST]} element={<UserGroups />} />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.USER_MANAGEMENT.USER_GROUPS_DETAILS}
          element={
            <RoleRoute
              roles={[ROLES.ARCHIVIST]}
              element={<UserGroupDetails />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.USER_MANAGEMENT.USER_DETAILS}
          element={
            <RoleRoute
              roles={[ROLES.ARCHIVIST]}
              element={<UserGroupUserDetails />}
            />
          }
        />
        {/* Owner Routes */}
        <Route
          path={ROUTES.DASHBOARD.OWNER_USER_MANAGEMENT}
          element={
            <RoleRoute
              roles={[ROLES.OWNER]}
              element={<OwnerUserManagement />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.OWNER_USER_CREATE}
          element={
            <RoleRoute roles={[ROLES.OWNER]} element={<CreateUpdateUser />} />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.OWNER_USER_EDIT}
          element={
            <RoleRoute
              roles={[ROLES.OWNER]}
              element={<CreateUpdateUser mode="update" />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.OWNER_USER_VIEW}
          element={
            <RoleRoute
              roles={[ROLES.OWNER, ROLES.MANAGER]}
              element={<ViewUser />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.FILES}
          element={<RoleRoute roles={[ROLES.OWNER]} element={<AllFiles />} />}
        />
        <Route
          path={ROUTES.DASHBOARD.DOCUMENTS.MY_DOCUMENTS}
          element={
            <RoleRoute
              roles={[
                ROLES.ARCHIVIST,
                ROLES.OWNER,
                ROLES.TEAM_MEMBER,
                ROLES.MANAGER,
              ]}
              element={<MyDocuments />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.DOCUMENTS.CREATE}
          element={
            <RoleRoute
              roles={[
                ROLES.OWNER,
                ROLES.TEAM_MEMBER,
                ROLES.MANAGER,
                ROLES.ARCHIVIST,
              ]}
              element={<DocumentCreate />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.DOCUMENTS.TRACK_REVIEWS}
          element={
            <RoleRoute
              roles={[ROLES.OWNER, ROLES.TEAM_MEMBER, ROLES.MANAGER]}
              element={<MyTasks />}
            />
          }
        >
          {/* Nested routes for MyTasks */}
          <Route index element={<My_Tasks_documents />} />
          <Route path="my-documents" element={<My_Tasks_documents />} />
          <Route path="my-master-files" element={<My_tasks_MasterFiles />} />
        </Route>
        <Route
          path={ROUTES.DASHBOARD.MASTER_FILES}
          element={
            <RoleRoute
              roles={[
                ROLES.TEAM_MEMBER,
                ROLES.MANAGER,
                ROLES.OWNER,
                ROLES.ARCHIVIST,
                ROLES.OBSERVER,
              ]}
              element={<MasterFiles />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.REGISTERS}
          element={
            <RoleRoute
              roles={[ROLES.MANAGER, ROLES.OWNER]}
              element={<Registers />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.HELPDESK}
          element={
            <RoleRoute
              roles={[
                ROLES.SUPER_ADMIN,
                ROLES.ARCHIVIST,
                ROLES.OWNER,
                ROLES.MANAGER,
                ROLES.TEAM_MEMBER,
              ]}
              element={<Helpdesk />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.TENANT_MANAGEMENT.LIST}
          element={
            <RoleRoute
              roles={[ROLES.SUPER_ADMIN]}
              element={<TenantManagement />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.TENANT_MANAGEMENT.CREATE}
          element={
            <RoleRoute
              roles={[ROLES.SUPER_ADMIN]}
              element={<TenantManagementCreate />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.TENANT_MANAGEMENT.EDIT}
          element={
            <RoleRoute
              roles={[ROLES.SUPER_ADMIN]}
              element={<TenantManagementCreate />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.TENANT_MANAGEMENT.VIEW}
          element={
            <RoleRoute
              roles={[ROLES.SUPER_ADMIN]}
              element={<TenantManagementView />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.SUBSCRIPTION_BILLING}
          element={
            <RoleRoute
              roles={[ROLES.SUPER_ADMIN]}
              element={<SubscriptionBilling />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.SUBSCRIPTION_BILLING_VIEW_INVOICES}
          element={
            <RoleRoute
              roles={[ROLES.SUPER_ADMIN, ROLES.ARCHIVIST]}
              element={<ViewInvoices />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.SUBSCRIPTION_BILLING_INVOICE_DETAILS}
          element={
            <RoleRoute
              roles={[ROLES.SUPER_ADMIN, ROLES.ARCHIVIST]}
              element={<InvoiceDetails />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.RECORDS}
          element={
            <RoleRoute
              roles={[
                ROLES.ARCHIVIST,
                ROLES.OWNER,
                ROLES.MANAGER,
                ROLES.TEAM_MEMBER,
                ROLES.OBSERVER,
              ]}
              element={<Records />}
            />
          }
        />
        <Route
          path={ROUTES.DASHBOARD.QUALITY_ISSUES}
          element={
            <RoleRoute roles={[ROLES.MANAGER]} element={<QualityIssues />} />
          }
        />
      </Route>

      {/* Redirect root to dashboard or login */}
      <Route
        path="/"
        element={<Navigate to={ROUTES.DASHBOARD.HOME} replace />}
      />
      <Route path="*" element={<Navigate to={ROUTES.AUTH.LOGIN} replace />} />
    </Routes>
  );
};

export default AppRoutes;
